import React, { useContext, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import AuthenticationContext from "../shared/AuthenticationContext";
import AuthenticationService from "../../services/AuthenticationService";
import CompanyService from "../../services/CompanyService";
import { TranslationService } from "../../services/TranslationService";
import ModalService from "../shared/bootstrap/modal/Modal";
import { getRememberCacheValue } from "./Logon";
import { StorageService } from "../../services/StorageService";
import Form from "./components/Form";
import "./logonModalStyles.css";

function SessionExpiredFormUI(props: {
  initialEmail: string;
  initialRemember: boolean;
  translate: typeof TranslationService.translate;
  currentLanguage: string;
  onSubmit: (data: {
    email: string;
    password: string;
    remember: boolean;
  }) => void;
  onLogout: () => Promise<void> | void;
  loading: boolean;
  error?: string;
}) {
  const {
    initialEmail,
    initialRemember,
    translate,
    currentLanguage,
    onSubmit,
    onLogout,
    loading,
    error
  } = props;

  const [formData, setFormData] = React.useState({
    email: initialEmail ?? "",
    password: "",
    remember: !!initialRemember
  });
  const [visiblePassword, setVisiblePassword] = React.useState(false);
  const [hasMessage, setHasMessage] = React.useState(!!error);
  const messageContent = error;

  React.useEffect(() => {
    setHasMessage(!!error);
  }, [error]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, type, checked, value } = e.target;
    setFormData((s) => ({
      ...s,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleVisiblePassword = () => setVisiblePassword((v) => !v);
  const handleLogIn = () => !loading && onSubmit(formData);

  return (
    <div className="modal-content logon-modal-content" aria-busy={loading}>
      <div
        className="modal-header"
        style={{ backgroundColor: "lightsteelblue" }}
      >
        <h2 className="h5 modal-title">{translate.SessionExpired}</h2>
      </div>

      <div className="modal-body">
        {hasMessage && messageContent && (
          <div className="alert alert-warning">{messageContent}</div>
        )}

        <div className="logon-modal">
          {/* Deshabilita todo mientras carga */}
          <fieldset
            disabled={loading}
            style={{ border: 0, padding: 0, margin: 0 }}
          >
            <Form back={false}>
              <h1>{translate.Login}</h1>

              {loading ? (
                <div
                  className="logon-loading-overlay"
                  role="status"
                  aria-live="polite"
                >
                  <i className="fas fa-spinner-third fa-spin fa-2x" />
                  <span className="visually-hidden">Loading…</span>
                </div>
              ) : (
                <>
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder={translate.Email}
                      id="email"
                      className="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      autoFocus
                      onKeyDown={(e) => e.key === "Enter" && handleLogIn()}
                    />
                    <i className="fas fa-user icono-input"></i>
                  </div>

                  <div className="input-group">
                    <input
                      type={visiblePassword ? "text" : "password"}
                      id="password"
                      name="password"
                      className="contrasena"
                      placeholder={translate.Password}
                      value={formData.password}
                      onChange={handleInputChange}
                      onKeyDown={(e) => e.key === "Enter" && handleLogIn()}
                    />
                    <i
                      onClick={handleVisiblePassword}
                      className={`fas fa-${visiblePassword ? "un" : ""}lock-alt icono-input`}
                      role="button"
                      tabIndex={0}
                    />
                  </div>

                  <div className="pass">
                    <a href={`/${currentLanguage}/recoverpassword`}>
                      {translate.PasswordForgotten}
                    </a>
                  </div>

                  <div className="recordar">
                    <input
                      type="checkbox"
                      id="remember"
                      name="remember"
                      checked={formData.remember}
                      onChange={handleInputChange}
                    />
                    <label htmlFor="remember">{translate.RememberMe}</label>
                  </div>

                  <div className="login-btn" id="loginbut">
                    <button
                      type="button"
                      className="btn btn-link-secondary"
                      onClick={onLogout}
                    >
                      {translate.Logout}
                      {loading && (
                        <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleLogIn}
                    >
                      {translate.Login.toUpperCase()}
                      {loading && (
                        <i className="fas fa-spinner-third fa-spin third ms-2"></i>
                      )}
                    </button>
                  </div>
                </>
              )}
            </Form>
          </fieldset>
        </div>
      </div>
    </div>
  );
}

const LogonModal = (props: { expired: boolean }) => {
  const { rememberUser, userEmailCache } = getRememberCacheValue();
  const history = useHistory();
  const { setCurrentAuthentication, doLogout } = useContext(
    AuthenticationContext
  );
  const { translate, currentLanguage } = TranslationService;

  const formDataSchema = useMemo(
    () =>
      yup.object({
        email: yup
          .string()
          .email(translate.LoginEmailIncorrect)
          .required(translate.LoginEmailMissing),
        password: yup.string().required(translate.LoginPasswordMissing)
      }),
    [translate]
  );

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);

  async function handleSubmit(data: {
    email: string;
    password: string;
    remember: boolean;
  }) {
    setError(undefined);
    try {
      await formDataSchema.validate(data, { abortEarly: true });
      setLoading(true);

      const userData = await AuthenticationService.login(
        data.email,
        data.password
      );

      StorageService.setPreference("rememberUser", data.remember);
      StorageService.setPreference(
        "userEmailCache",
        data.remember ? data.email : ""
      );

      if (userData instanceof Error) setError(translate.Network);
      else if (userData.response === "LoginIncorrect")
        setError(translate.LoginIncorrect);
      else if (userData.response === "NextBlock") setError(translate.NextBlock);
      else if (userData.response === "2AF") {
        const queryParams = new URLSearchParams();
        queryParams.append("token", userData.twofadata);
        queryParams.append(
          "returnUrl",
          history.location.pathname + history.location.search
        );
        if (CompanyService.getUserEmail() !== data.email) {
          window.location.href = `/${currentLanguage}/complete-sign-in?${queryParams.toString()}`;
        }
        return;
      } else if (userData.response === "OK") {
        const companyData = await AuthenticationService.companyLogin(
          userData,
          data.remember
        );
        if (companyData instanceof Error) setError(translate.Network);
        else {
          setCurrentAuthentication(companyData);
        }
        if (CompanyService.getUserEmail() !== data.email)
          window.location.reload();
      } else if (userData.response === "Blocked") setError(translate.Blocked);
    } catch (err: any) {
      setError(err?.errors?.[0] ?? translate.LoginIncorrect);
    } finally {
      setLoading(false);
    }
  }

  // Nuevo: logout con loader
  const handleLogout = async () => {
    try {
      setLoading(true);
      await Promise.resolve(doLogout());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (props.expired) {
      ModalService.showModal(
        <div className="genericModal">
          <div className="modal-dialog modal-dialog-centered">
            <SessionExpiredFormUI
              initialEmail={userEmailCache ?? ""}
              initialRemember={!!rememberUser}
              translate={TranslationService.translate}
              currentLanguage={currentLanguage}
              onSubmit={handleSubmit}
              onLogout={handleLogout}
              loading={loading}
              error={error}
            />
          </div>
        </div>
      );
    } else {
      ModalService.hideModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.expired,
    currentLanguage,
    rememberUser,
    userEmailCache,
    loading,
    error
  ]);

  return null;
};

export default LogonModal;
